import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  Button,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Crop as CropIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";

interface EnhancedGalleryManagerProps {
  images: string[];
  title: string;
  type: "showcase" | "studentWork";
  isLive?: boolean;
  selectedImages: number[];
  onToggleSelection: (index: number) => void;
  onToggleSelectAll: () => void;
  onDelete: (index: number) => void;
  onBulkDelete: () => void;
  onCrop: (index: number) => void;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (index: number) => void;
  isValidImageUrl: (url: string) => boolean;
}

const EnhancedGalleryManager: React.FC<EnhancedGalleryManagerProps> = ({
  images,
  title,
  type,
  isLive = false,
  selectedImages,
  onToggleSelection,
  onToggleSelectAll,
  onDelete,
  onBulkDelete,
  onCrop,
  onDragStart,
  onDragOver,
  onDrop,
  isValidImageUrl,
}) => {
  const validImages = images.filter((img) => isValidImageUrl(img));
  const hasImages = validImages.length > 0;
  const allSelected = hasImages && selectedImages.length === validImages.length;

  if (!hasImages) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Typography variant="subtitle2" color={isLive ? "success.main" : "info.main"}>
          {isLive ? "✓ Currently Live" : "📷 Staged"} ({validImages.length} images)
        </Typography>
        
        {hasImages && (
          <>
            <Tooltip title={allSelected ? "Deselect All" : "Select All"}>
              <IconButton
                size="small"
                onClick={onToggleSelectAll}
                sx={{ color: allSelected ? "primary.main" : "text.secondary" }}
              >
                {allSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
              </IconButton>
            </Tooltip>

            {selectedImages.length > 0 && (
              <Chip
                label={`${selectedImages.length} selected`}
                size="small"
                color="primary"
                onDelete={onBulkDelete}
                deleteIcon={<DeleteSweepIcon />}
              />
            )}
          </>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
          gap: 1,
        }}
      >
        {validImages.map((image, index) => (
          <Box
            key={index}
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(index)}
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              border: "2px solid",
              borderColor: isLive ? "success.main" : "info.main",
              borderStyle: isLive ? "solid" : "dashed",
              cursor: "grab",
              transition: "all 0.2s",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: 3,
              },
              ...(selectedImages.includes(index) && {
                border: "3px solid",
                borderColor: "primary.main",
              }),
            }}
          >
            {/* Image */}
            <img
              src={isLive ? `${image}?t=${Date.now()}` : image}
              alt={`${title} ${index + 1}`}
              loading="lazy"
              style={{
                width: "100%",
                height: 120,
                objectFit: "cover",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />

            {/* Drag Handle */}
            <Box
              sx={{
                position: "absolute",
                top: 4,
                left: 4,
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                color: "white",
                borderRadius: 1,
                cursor: "grab",
                "&:active": {
                  cursor: "grabbing",
                },
              }}
            >
              <DragIcon fontSize="small" />
            </Box>

            {/* Index */}
            <Box
              sx={{
                position: "absolute",
                top: 4,
                left: 32,
                backgroundColor: isLive ? "success.main" : "info.main",
                color: "white",
                borderRadius: "50%",
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: "bold",
              }}
            >
              {index + 1}
            </Box>

            {/* Status Badge */}
            <Box
              sx={{
                position: "absolute",
                bottom: 4,
                left: 4,
                backgroundColor: isLive ? "success.main" : "info.main",
                color: "white",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: "0.6rem",
                fontWeight: "bold",
              }}
            >
              {isLive ? "LIVE" : "STAGED"}
            </Box>

            {/* Checkbox */}
            <Checkbox
              checked={selectedImages.includes(index)}
              onChange={() => onToggleSelection(index)}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                padding: 0,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
              size="small"
            />

            {/* Action Buttons */}
            <Box
              sx={{
                position: "absolute",
                bottom: 2,
                right: 2,
                display: "flex",
                gap: 0.5,
              }}
            >
              {/* Crop Button */}
              <Tooltip title="Crop Image">
                <IconButton
                  onClick={() => onCrop(index)}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(25, 118, 210, 0.9)",
                    color: "white",
                    width: 28,
                    height: 28,
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 1)",
                    },
                  }}
                >
                  <CropIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>

              {/* Delete Button */}
              <Tooltip title="Delete Image">
                <IconButton
                  onClick={() => onDelete(index)}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(211, 47, 47, 0.9)",
                    color: "white",
                    width: 28,
                    height: 28,
                    "&:hover": {
                      backgroundColor: "rgba(211, 47, 47, 1)",
                    },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default EnhancedGalleryManager;
